// Where the user came from, for "back" controls that should return to the
// previous screen rather than a hard-coded destination.
//
// React Navigation can't answer this for us here: the drawer's own history is
// unreliable — MainMenu navigates with CommonActions.reset(), which wipes it,
// and screens that redirect on mount leave entries that bounce straight back.
// So we record it ourselves off the one event that always fires,
// NavigationContainer's onStateChange.
//
// Params are recorded alongside the name. A route like ClubProfile
// ('clubs/profile/:clubId') is meaningless without them — navigating back by
// name alone lands on a screen whose `route.params.clubId` is undefined.

import create from 'zustand';

export interface VisitedRoute {
  name: string;
  params?: Record<string, any>;
}

// Enough depth to skip past a screen or two that redirect on mount, without
// holding onto history nobody will ever walk back through.
const MAX_ENTRIES = 10;

// For most screens, a same-name re-fire of onStateChange really is a
// params-only refresh (e.g. switching tabs within School of Chess) and should
// update the existing entry in place rather than push a new one. PlayerProfile
// is the exception: the same screen name is reused for every player, so
// navigating from one player's profile to another's (a "chain") is a genuine
// new visit, not a refresh -- keyed on this param, a name match only
// collapses when the identity param also matches.
const ROUTE_IDENTITY_PARAM: Record<string, string> = {
  PlayerProfile: 'username',
};

interface NavHistoryState {
  /** Most recent last. */
  stack: VisitedRoute[];
  /** Called on every navigation state change with the resolved route. */
  setRoute: (name: string, params?: Record<string, any>) => void;
  /**
   * Most recent entry that isn't the current screen and isn't excluded.
   * `exclude` exists for screens that redirect back to the caller — walking
   * into one of those is an immediate bounce.
   */
  getPrevious: (exclude?: string[]) => VisitedRoute | null;
}

export const useNavHistoryStore = create<NavHistoryState>((set, getState) => ({
  stack: [],

  setRoute: (name, params) => {
    if (!name) {
      return;
    }
    const {stack} = getState();
    const last = stack[stack.length - 1];
    // Params-only changes (e.g. switching tabs within School of Chess) re-fire
    // onStateChange with the same route. Treating those as navigation would
    // make "back" point at the screen you're already on — just refresh the
    // params on the existing entry instead. Routes with an identity param
    // (see ROUTE_IDENTITY_PARAM) only qualify when that param also matches.
    const identityParam = ROUTE_IDENTITY_PARAM[name];
    const sameIdentity =
      !identityParam || last?.params?.[identityParam] === params?.[identityParam];
    if (last?.name === name && sameIdentity) {
      const next = stack.slice(0, -1);
      next.push({name, params});
      set({stack: next});
      return;
    }
    set({stack: [...stack, {name, params}].slice(-MAX_ENTRIES)});
  },

  getPrevious: (exclude = []) => {
    const {stack} = getState();
    const currentEntry = stack[stack.length - 1];
    const current = currentEntry?.name;
    // Same identity-aware comparison as setRoute -- otherwise this would
    // skip a *different* player's PlayerProfile entry too (matches on name
    // alone), defeating setRoute's whole point of keeping them distinct.
    const identityParam = current ? ROUTE_IDENTITY_PARAM[current] : undefined;
    const currentIdentity = identityParam
      ? currentEntry?.params?.[identityParam]
      : undefined;
    for (let i = stack.length - 2; i >= 0; i--) {
      const entry = stack[i];
      const isSameAsCurrent =
        entry.name === current &&
        (!identityParam || entry.params?.[identityParam] === currentIdentity);
      if (!isSameAsCurrent && !exclude.includes(entry.name)) {
        return entry;
      }
    }
    return null;
  },
}));

export default useNavHistoryStore;
